/**
 * Types et interfaces TypeScript pour l'API Clementino (Logistique & Expeditions).
 * Alignement strict sur la collection MOPOZZI API.
 */

export type RawClementinoStatus =
  | string
  | { id?: number; code?: string; libelle?: string; nom?: string; name?: string; status?: any }
  | null
  | undefined;

/**
 * Extrait proprement et de manière infaillible une chaîne de caractères représentant un statut,
 * quel que soit le format renvoyé par le backend Clementino / Mopozzi :
 * - string : "RECEPTIONNE"
 * - objet : { id: 1, code: "RECEPTIONNE", libelle: "Réceptionné" }
 * - objet imbriqué : { status: { code: "..." } }
 * - number, null, undefined
 */
export function normalizeStatus(st: any): string {
  if (st === null || st === undefined) return "";
  if (typeof st === "string") return st.trim();
  if (typeof st === "number") return String(st);
  if (typeof st === "object") {
    const val =
      st.code ||
      st.libelle ||
      st.nom ||
      st.name ||
      st.status ||
      st.statut ||
      st.valeur ||
      st.label ||
      (st.id !== undefined && st.id !== null ? String(st.id) : "");
    if (typeof val === "string") return val.trim();
    if (typeof val === "object" && val !== null) {
      return normalizeStatus(val);
    }
    return String(val || "");
  }
  return String(st).trim();
}

export interface ClementinoColisImage {
  path: string;
  caption?: string;
}

export interface ClementinoColis {
  id?: number;
  description: string;
  natureColis?: number | string;
  valeurMarchande?: number;
  poidsColis?: number;
  unitePoids?: string;
  longueur?: number;
  uniteLongueur?: string;
  largeur?: number;
  uniteLargeur?: string;
  hauteur?: number;
  uniteHauteur?: string;
  nombre?: number;
  images?: ClementinoColisImage[];
}

export interface ClementinoTrackingEvent {
  id?: number | string;
  status: RawClementinoStatus;
  date: string;
  commentaire?: string;
  localisationGps?: string;
  agent?: string;
  retirerPar?: string;
  retirerParTypePiecee?: string;
  retirerParNumPiece?: string;
}

export interface ClementinoExpedition {
  id: number;
  numLt?: string;
  numLettreTransport?: string;
  numExpedition?: string;
  numeroExpedition?: string;
  contactExpediteur: string;
  telExpediteur: string;
  adresseExpediteur?: string;
  contactDestinataire: string;
  telDestinataire: string;
  adresseDestination?: string;
  villeExpedition: number | string | { id?: number; nom?: string; name?: string };
  villeDestination: number | string | { id?: number; nom?: string; name?: string };
  quartierDepart?: number | string;
  quartierExpeditionNom?: string;
  quartierDestination?: number | string | null;
  quartierDestinationNom?: string;
  pointDeRepere?: string;
  instructionsLivraison?: string;
  agenceExpedition?: number | string | { id?: number; nom?: string };
  agenceDestination?: number | string | { id?: number; nom?: string };
  status: RawClementinoStatus;
  statut?: RawClementinoStatus;
  montant: number;
  montantARecupererAuClientFinal?: number;
  fraisCommercant?: number;
  remise?: number;
  payeur?: "EXPEDITEUR" | "DESTINATAIRE" | string;
  modePaiement?: "ESPECE" | "ORANGE_MONEY" | "MTN_MOMO" | "AUTRE" | string;
  numeroTelPaiement?: string;
  dateLivraisonSouhaite?: string;
  heureLivraisonSouhaite?: string;
  pointDeCollecte?: string;
  validePourLivraison?: boolean;
  colis?: ClementinoColis[] | ClementinoColis;
  nbTotalColis?: number | string;
  natureColis?: string;
  trackingEvents?: ClementinoTrackingEvent[];
  coursier?: {
    id: number;
    nom: string;
    prenoms?: string;
    telephone?: string;
  } | null;
  dateCreation?: string;
  createdAt?: string;
  type?: string;
  paysExpedition?: string | number | { id?: number; alpha2?: string; nomFr?: string };
  paysDestination?: string | number | { id?: number; alpha2?: string; nomFr?: string };
}

export interface FiltresExpeditions {
  date_debut?: string;
  date_fin?: string;
  status?: string;
  type?: string;
  client?: string | number;
  ville_expedition?: string | number;
  agence_expedition?: string | number;
  ville_destination?: string | number;
  agence_destination?: string | number;
  pays_expedition?: string | number;
  pays_destination?: string | number;
  validePourLivraison?: boolean;
  search?: string;
}

export interface CreateExpeditionDTO {
  contactExpediteur: string;
  telExpediteur: string;
  adresseExpediteur?: string;
  contactDestinataire: string;
  telDestinataire: string;
  adresseDestination?: string;
  villeExpedition: number;
  villeDestination: number;
  quartierDepart?: number;
  quartierExpeditionNom?: string;
  quartierDestination?: number | null;
  quartierDestinationNom?: string;
  pointDeRepere?: string;
  instructionsLivraison?: string;
  agenceExpedition?: number;
  agenceDestination?: number;
  montant: number;
  montantARecupererAuClientFinal?: number;
  fraisCommercant?: number;
  remise?: number;
  payeur?: "EXPEDITEUR" | "DESTINATAIRE" | string;
  modePaiement?: string;
  numeroTelPaiement?: string;
  dateLivraisonSouhaite?: string;
  pointDeCollecte?: string;
  colis: ClementinoColis[];
}

export interface ChangeStatusDTO {
  status: string;
  localisationGpsExpediteur?: string;
  localisationGpsDestination?: string;
  localisationGps?: string;
  commentaire?: string;
  retirerPar?: string;
  retirerParTypePiecee?: string;
  retirerParNumPiece?: string;
}

export interface PaymentInitiationDTO {
  numLts: string[];
  channel: string;
  phoneNumber: string;
}

export interface PaymentSessionResult {
  reference: string;
  externalTransactionId?: string;
  amount?: number;
  currency?: string;
  status?: string;
  channel?: string;
}

export interface VoyageEtape {
  depart: number;
  agence_depart: number;
  arrive: number;
  agence_arrive: number;
  moyen_transport: "ROUTE" | "TRAIN" | "VOL" | string;
  chauffeur?: string;
  vehicule?: string;
  autre_vehicule?: string;
  numero_train?: string;
  compagnie?: string;
  numero_vol?: string;
}

export interface VoyageEntity {
  id: number;
  expeditions: string;
  date_depart: string;
  heure_depart?: string;
  minute_depart?: string;
  agence_expedition?: number;
  agence_destination?: number;
  etape?: VoyageEtape[];
  etapes?: VoyageEtape[];
  statut?: string;
}

export interface TourneeEntity {
  id: number;
  coursierId?: number;
  coursier?: {
    id: number;
    nom: string;
    telephone?: string;
  };
  expeditions: (string | ClementinoExpedition)[];
  statut: string;
  dateCreation?: string;
  dateCloture?: string;
  vehicule?: string;
}

export interface TicketClementino {
  id: number;
  type: "RECLAMATION" | "INCIDENT" | "SUGGESTION" | string;
  sujet: string;
  description: string;
  numLt?: string;
  numLettreTransport?: string;
  nomPlaignant?: string;
  telPlaignant?: string;
  emailPlaignant?: string;
  status: RawClementinoStatus;
  statut?: RawClementinoStatus;
  dateCreation?: string;
  createdAt?: string;
  note?: string;
}

export interface AgenceReferentiel {
  id: number;
  nom: string;
  code?: string;
  villeId?: number;
}

export interface VilleReferentiel {
  id: number;
  nom: string;
  paysId?: number;
}

export interface QuartierReferentiel {
  id: number;
  nom: string;
  villeId: number;
}

export interface PaysReferentiel {
  id: number;
  nom: string;
  code?: string;
}

export interface NatureColisReferentiel {
  id: number;
  nom: string;
}
